import React from "react";
//#region types
import { EditableFormInnerProps, GenericFormSection, WizardTabProps } from "models/propTypes/genericFormProps";
import { IconDefinitionConstants, RouteConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress, Grid } from "@mui/material";
import useCheckVisibility from "./hooks/useCheckVisibility";
import useCheckHasErrors from "./hooks/useCheckHasErrors";
import useWizardContext from "../wizard/context/useWizardContext";
import useFocusField from "./hooks/useFocusField";
import Icon from "../icon";
import GenericFormInner from "./genericFormInner";
import { useFormikContext } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { FormHelper, LocalizationHelperInstance } from "utils/helpers";
import { scrollContainerToAnchor } from "utils/helpers";
import clsx from "clsx";

function EditableFormInnerWizard<T extends Record<string, any>>(props: EditableFormInnerProps<T>) {
  const { values, submitCount } = useFormikContext();
  const { getFieldHidden } = useCheckVisibility<T>();

  const navigate = useNavigate();
  const location = useLocation();

  const fields = (props.fields as GenericFormSection<T>[])?.filter((field) => !getFieldHidden(field.hidden));
  const focusField = props.focusField?.split(".");

  const { getHasErrors } = useCheckHasErrors<T>(true);

  const [submitted, setSubmitted] = React.useState(false);
  const [previousStep, setPreviousStep] = React.useState<number>();
  const [currentStep, setCurrentStep] = React.useState<{ step: number; expanded: boolean }>({
    step: 0,
    expanded: true,
  });
  const [visitedSteps, setVisitedSteps] = React.useState<Set<number>>(new Set([0]));

  const wizardContext = useWizardContext();
  const showStepIcons = props.wizardConfig?.showStepIcons ?? false;

  // Set as current step and scroll to the section if the section is in the URL
  const timeOutForDownwardScroll = 350;
  const timeOutForUpwardScroll = 200;
  const timeOutForCurrentStepCollapse = 100;
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get(RouteConstants.QueryParamStrings.Section);
    if (section) {
      const index = fields.findIndex((field) => FormHelper.getSectionNavKey(field) === section);

      setTimeout(
        () => {
          scrollContainerToAnchor(`${section}-wizardAnchor`, { behavior: "smooth", block: "start" });
        },
        index > currentStep.step ? timeOutForDownwardScroll : timeOutForUpwardScroll
      );

      // Only change step if it's different from current step, or if current step is not expanded
      // This prevents the toggle conflict when anchor link points to step 1 (index 0) on page load
      if (index !== currentStep.step || !currentStep.expanded) {
        onCurrentStepChange(index);
      }
    }
  }, [location.search]);

  React.useEffect(() => {
    if (wizardContext && wizardContext.currentStep !== currentStep.step) {
      if (fields.length > wizardContext.currentStep || wizardContext.currentStep < 0)
        onCurrentStepChange(wizardContext.currentStep);
      else wizardContext.setCurrentStep(currentStep.step);
    }
  }, [wizardContext?.currentStep]);

  const [initialFocusTabApplied, setInitialFocusTabApplied] = React.useState<boolean>(false);
  const { hasFocusField } = useFocusField<T>(focusField && focusField[0]);

  React.useEffect(() => {
    if (fields.length !== wizardContext?.numOfSteps) {
      wizardContext?.setNumOfSteps(fields.length);
    }
  }, [fields]);

  React.useEffect(() => {
    if (submitCount && !submitted) setSubmitted(true);
  }, [submitCount]);

  React.useEffect(() => {
    if (!initialFocusTabApplied && focusField)
      fields.some((section, index) => {
        if (hasFocusField(section)) {
          onCurrentStepChange(index);
          setInitialFocusTabApplied(true);
          return true;
        } else return false;
      });
  }, [focusField]);

  React.useEffect(() => {
    if (wizardContext && wizardContext.currentStep !== currentStep.step) wizardContext.setCurrentStep(currentStep.step);
  }, [currentStep]);

  const navigateToUrl = (navKey: string) => {
    const queryParams = new URLSearchParams({
      [RouteConstants.QueryParamStrings.Section]: navKey,
    }).toString();
    navigate(`${location.pathname}?${queryParams}`, { replace: true });
  };

  const getIsNavigationViaUrl = (index: number) => {
    const params = new URLSearchParams(location.search);
    const sectionFromParams = params.get(RouteConstants.QueryParamStrings.Section);
    const section = fields[index];
    const navKey = section?.navKey;
    return { isNavigationViaUrl: navKey && sectionFromParams !== navKey, navKey: navKey };
  };

  const onCurrentStepChange = (index: number) => {
    const { isNavigationViaUrl, navKey } = getIsNavigationViaUrl(index);
    if (isNavigationViaUrl && navKey) {
      navigateToUrl(navKey);
      return;
    }

    if (!visitedSteps.has(index)) {
      setVisitedSteps(new Set<number>(visitedSteps).add(index));
    }

    // Lock scroll position during accordion transition to prevent browser auto-scroll
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const preventAutoScroll = () => {
      window.scrollTo(0, currentScrollTop);
    };

    // Prevent any scroll changes during accordion expansion
    window.addEventListener("scroll", preventAutoScroll, { passive: false });

    // To keep the current accordion expanded while the step is changed to avoid flickering
    setPreviousStep(currentStep.step);
    setCurrentStep((prevStep) => ({ step: index, expanded: prevStep.step === index ? !prevStep.expanded : true }));

    setTimeout(() => {
      setPreviousStep(undefined);
      // Remove scroll lock after accordion transition completes
      window.removeEventListener("scroll", preventAutoScroll);
    }, timeOutForCurrentStepCollapse + 50);
  };

  return (
    <Grid container item direction="column" xs>
      {fields.map((tab, index) => {
        const sectionKey = FormHelper.getSectionNavKey(tab);
        const extraProps = (tab.extraProps ?? {}) as WizardTabProps;
        const hasErrors = getHasErrors(fields[index]);
        const descriptionText = tab.descriptionText
          ? typeof tab.descriptionText === "string"
            ? LocalizationHelperInstance.getFormattedMessage(tab.descriptionText)
            : tab.descriptionText
          : "";
        return (
          <React.Fragment key={`sectionContainer-${index}`}>
            <div id={`${sectionKey}-wizardAnchor`} className={cssExports.scrollAnchor} aria-hidden="true"></div>
            <Accordion
              key={`sectionHeader-${index}`}
              className={clsx(cssExports.wizardStep)}
              expanded={(index === currentStep.step && currentStep.expanded) || index === previousStep}
              classes={{
                expanded: cssExports.expandedStep,
              }}
            >
              <AccordionSummary
                id={`${sectionKey}-hdr`}
                classes={{
                  root: clsx(
                    cssExports.wizardStepHeader,
                    extraProps?.isContentLoading
                      ? ""
                      : index === currentStep.step && currentStep.expanded
                        ? cssExports.currentStep
                        : (submitted || visitedSteps.has(index)) && hasErrors
                          ? cssExports.errorStep
                          : !hasErrors && !(showStepIcons && (tab as any)?.icon)
                            ? cssExports.completeStep
                            : ""
                  ),
                  content: cssExports.titleContent,
                  expanded: cssExports.expanded,
                  expandIconWrapper: cssExports.expandIcon,
                }}
                expandIcon={<></>}
                onClick={onCurrentStepChange.bind(null, index)}
              >
                <Grid container direction="row" wrap="nowrap" justifyContent="space-between">
                  <Grid item xs className={cssExports.wizardStepHeaderTitleContentWrapper}>
                    <Grid container item alignItems="center" spacing={2}>
                      <Grid item>
                        <Grid item className={clsx(cssExports.wizardStepNumber)}>
                          {extraProps?.isContentLoading ? (
                            <CircularProgress size={16} thickness={6} className={cssExports.loadingIcon} />
                          ) : showStepIcons && (tab as any)?.icon ? (
                            <Icon icon={(tab as any).icon} />
                          ) : !hasErrors && index !== currentStep.step ? (
                            <Icon icon={IconDefinitionConstants.WValidateCheck} />
                          ) : (
                            <span>{index + 1}</span>
                          )}
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        xs
                        container
                        direction={"column"}
                        justifyContent={"center"}
                        className={cssExports.wizardStepHeaderTitleContainer}
                      >
                        {tab.label && (
                          <Grid item container className={cssExports.wizardStepHeaderTitle}>
                            {LocalizationHelperInstance.getFormattedMessage(tab.label)}
                          </Grid>
                        )}
                        <Grid item container className={cssExports.wizardStepHeaderSubTitle}>
                          <span>
                            {!hasErrors
                              ? extraProps?.summaryFormatter
                                ? (extraProps.summaryFormatter(values) ?? descriptionText)
                                : ""
                              : descriptionText}
                          </span>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    {index === currentStep.step && currentStep.expanded ? (
                      ""
                    ) : visitedSteps.has(index) && hasErrors ? (
                      <span className={cssExports.wizardStepErrorText}>
                        {LocalizationHelperInstance.getFormattedMessage(
                          TranslationKeys.Common.SomethingIsMissingReviewAgain
                        )}
                      </span>
                    ) : !hasErrors ? (
                      <span className={cssExports.wizardStepDoneText}>
                        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Done)}
                      </span>
                    ) : (
                      ""
                    )}
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails
                classes={{
                  root: cssExports.wizardStepContent,
                }}
                className={(tab as any)?.className}
              >
                <Grid container>
                  {(index === currentStep.step && currentStep.expanded) || index === previousStep ? (
                    <GenericFormInner<T>
                      {...props}
                      key={`genericFormInner-${index}`}
                      fields={tab.innerFields}
                      inline={tab.inline ?? props.inline}
                      enableControlLables={tab.enableControlLables ?? props.enableControlLables}
                      formGridLayout={tab.formGridLayout ?? props.formGridLayout}
                    />
                  ) : (
                    <></>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </React.Fragment>
        );
      })}
    </Grid>
  );
}

export default EditableFormInnerWizard;
